import { useCallback, useEffect, useState } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { getErrorText } from '../utils/httpErrors.js';

export const useApplications = ({ autoLoad = true } = {}) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);

      const response = await fetchWithAuth('/api/applications');

      if (!response.ok) {
        if (response.status === 401) {
          setLoading(false);
          return [];
        }
        if (response.status === 403) {
          setApplications([]);
          setError(null);
          setAccessDenied(true);
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setApplications(data);
      return data;
    } catch (err) {
      if (err.message === 'Unauthorized') {
        setLoading(false);
        return [];
      }
      console.error('Error fetching applications:', err);
      setError(getErrorText(err));
      setApplications([]);
      setAccessDenied(false);
      return [];
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!autoLoad) return undefined;
    fetchApplications();
    return undefined;
  }, [autoLoad, fetchApplications]);

  return {
    applications,
    loading: autoLoad && (loading || !hasLoaded),
    error,
    accessDenied,
    reload: fetchApplications,
  };
};
